"use client";

import type React from "react";
import { createPortal } from "react-dom";
import { MarkdownText } from "./MarkdownText";

const POPOVER_WIDTH = 340;
const POPOVER_MAX_HEIGHT = 240;
const VIEWPORT_MARGIN = 8;

function getPopoverPosition(anchorRect: DOMRect): React.CSSProperties {
  const idealLeft = anchorRect.left + anchorRect.width / 2 - POPOVER_WIDTH / 2;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(idealLeft, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN),
  );
  const shouldFlipAbove =
    window.innerHeight - anchorRect.bottom <
      POPOVER_MAX_HEIGHT + VIEWPORT_MARGIN &&
    anchorRect.top > POPOVER_MAX_HEIGHT + VIEWPORT_MARGIN;

  if (shouldFlipAbove) {
    // Anchor the tooltip's bottom edge to the anchor element's top.
    // This way the tooltip hugs the element regardless of actual content height.
    const bottom = window.innerHeight - anchorRect.top + VIEWPORT_MARGIN;
    return { left, bottom };
  }

  const top = Math.min(
    anchorRect.bottom + VIEWPORT_MARGIN,
    window.innerHeight - POPOVER_MAX_HEIGHT - VIEWPORT_MARGIN,
  );
  return { left, top };
}

function getBadgeClasses(tone?: string) {
  if (tone === "critical") return "bg-red-100 text-red-700";
  if (tone === "warning") return "bg-yellow-100 text-yellow-700";
  if (tone === "info") return "bg-indigo-100 text-indigo-700";
  return "bg-gray-100 text-gray-600";
}

interface CitationPopoverProps {
  anchorRect: DOMRect;
  badgeLabel?: string;
  badgeTone?: "critical" | "warning" | "info" | "neutral";
  title?: string;
  meta?: string;
  body: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

export function CitationPopover({
  anchorRect,
  badgeLabel,
  badgeTone = "neutral",
  title,
  meta,
  body,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: CitationPopoverProps) {
  if (typeof document === "undefined") return null;

  const position = getPopoverPosition(anchorRect);

  return createPortal(
    <div
      role="tooltip"
      className="fixed z-50"
      style={{
        ...position,
        width: POPOVER_WIDTH,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        aria-label="Open preview"
        className="flex max-h-[240px] w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
        onClick={onClick}
      >
        <div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
          <div className="flex items-center gap-2">
            {badgeLabel ? (
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getBadgeClasses(
                  badgeTone,
                )}`}
              >
                {badgeLabel}
              </span>
            ) : null}
            {meta ? (
              <span className="text-xs text-gray-500">{meta}</span>
            ) : null}
          </div>
          {title ? (
            <div className="mt-1 text-sm font-medium text-gray-800">
              {title}
            </div>
          ) : null}
        </div>
        <div className="min-h-0 overflow-y-auto p-3 text-sm text-gray-800">
          <MarkdownText mode="block">{body}</MarkdownText>
        </div>
      </button>
    </div>,
    document.body,
  );
}
